import { normalizePlanar } from "../core/math.ts";

export interface TouchInputSample {
  readonly moveX: number;
  readonly moveZ: number;
  readonly jumpPressed: boolean;
  readonly ascendHeld: boolean;
  readonly descendHeld: boolean;
  readonly sprintHeld: boolean;
  readonly interactPressed: boolean;
  readonly cycleSeatPressed: boolean;
  readonly toggleFlightPressed: boolean;
}

/**
 * DOM-free touch state. The UI writes control changes here and the fixed-step
 * input adapter samples a serializable value once per simulation tick.
 */
export class TouchInputAdapter {
  #moveX = 0;
  #moveZ = 0;
  #ascendHeld = false;
  #descendHeld = false;
  #sprintHeld = false;
  #jumpPressed = false;
  #interactPressed = false;
  #cycleSeatPressed = false;
  #toggleFlightPressed = false;

  setMovement(moveX: number, moveZ: number): void {
    const movement = normalizePlanar(
      Number.isFinite(moveX) ? moveX : 0,
      Number.isFinite(moveZ) ? moveZ : 0,
    );
    this.#moveX = movement.x;
    this.#moveZ = movement.z;
  }

  setJumpHeld(held: boolean): void {
    if (held && !this.#ascendHeld) {
      this.#jumpPressed = true;
    }
    this.#ascendHeld = held;
  }

  setDescendHeld(held: boolean): void {
    this.#descendHeld = held;
  }

  setSprintHeld(held: boolean): void {
    this.#sprintHeld = held;
  }

  pressInteract(): void {
    this.#interactPressed = true;
  }

  pressCycleSeat(): void {
    this.#cycleSeatPressed = true;
  }

  pressToggleFlight(): void {
    this.#toggleFlightPressed = true;
  }

  reset(): void {
    this.#moveX = 0;
    this.#moveZ = 0;
    this.#ascendHeld = false;
    this.#descendHeld = false;
    this.#sprintHeld = false;
    this.#jumpPressed = false;
    this.#interactPressed = false;
    this.#cycleSeatPressed = false;
    this.#toggleFlightPressed = false;
  }

  sample(): TouchInputSample {
    const sample = {
      moveX: this.#moveX,
      moveZ: this.#moveZ,
      jumpPressed: this.#jumpPressed,
      ascendHeld: this.#ascendHeld,
      descendHeld: this.#descendHeld,
      sprintHeld: this.#sprintHeld,
      interactPressed: this.#interactPressed,
      cycleSeatPressed: this.#cycleSeatPressed,
      toggleFlightPressed: this.#toggleFlightPressed,
    };
    this.#jumpPressed = false;
    this.#interactPressed = false;
    this.#cycleSeatPressed = false;
    this.#toggleFlightPressed = false;
    return sample;
  }
}
